
import { Team } from '@/classes/auth';
import { Plan, Subscription } from '@/classes/stripe';
import DowngradeRestrictedModal from '@/components/Modals/Account/DowngradeRestrictedModal.vue';
import ReactivateSubscription from '@/components/Modals/Account/ReactivateSubscription.vue';
import { Currencies, CurrencyCode, CurrencySymbols, Locales } from '@/Models/Localization';
import Currency from '@/Models/PrimitiveModels/Currency/Currency';
import KeyValueItem from '@/Models/PrimitiveModels/KeyValueItem';
import { trackCheckoutNowButtonPricingPageStep1 } from '@/Services/GoogleTagManager';
import { trackCheckoutNowButtonPricingPage } from '@/Services/Snowplow/SnowplowService';
import { labelForPlanState } from '@/Services/Subscription/PlanViewService';
import StripeSubscriptionService from '@/Services/Subscription/StripeSubscriptionService';
import store from '@/store';
import { AccountState } from '@/store/account';
import { Component, Prop, Vue } from 'vue-property-decorator';
import { mapGetters, mapState } from 'vuex';

interface ExchangeRateInterface {
  key: string;
  value: { exchangeRate: number; locale: string; symbol: string };
}

@Component({
  components: {
    DowngradeRestrictedModal,
    ReactivateSubscription,
  },
  computed: {
    ...mapState<AccountState>('account', {
      id: (state: AccountState) => state.account?.id || [],
      qrcodes: (state: AccountState) => state.account?.qrcodes || [],
    }),
    ...mapGetters('auth', {
      team: 'team',
    }),
  },
})
export default class PlanTile extends Vue {
  @Prop({ default: () => new Plan() })
  plan!: Plan;

  @Prop({ default: () => new StripeSubscriptionService() })
  stripeSubscriptionService!: StripeSubscriptionService;

  @Prop({ default: Currencies.EUR })
  currencyIso2!: string;

  @Prop({ default: 'month' })
  selectedInterval!: string;

  @Prop({ default: false })
  loading!: boolean;

  get activeSubscription(): Subscription {
    return store.state.subscription.activeSubscription;
  }

  team!: Team;

  openDowngradeModal = false;
  qrcodes: any;

  reactivateModalShow = false;
  initialCurrency: CurrencyCode = '';
  initialPlanPrice: number = 0;

  /**
   * All exchange-rate values are based on 1EUR so we can easily multiply
   * to get new value.
   */
  exchangeRates: Array<KeyValueItem> = [
    {
      key: Currencies.USD,
      value: {
        exchangeRate: 1.041879, // 1EUR => USD
        locale: Locales.en_US,
        symbol: CurrencySymbols.USD,
      },
    },
    {
      key: Currencies.GBP,
      value: {
        exchangeRate: 0.875723, // 1EUR => GBP
        locale: Locales.en_GB,
        symbol: CurrencySymbols.GBP,
      },
    },
    {
      key: Currencies.BRL,
      value: {
        exchangeRate: 6.15, // 1EUR => BRL
        locale: Locales.pt_BR,
        symbol: CurrencySymbols.BRL,
      },
    },
  ];

  // Limits of this plan as a clean list, null values won't be shown
  featureList: Array<{ title: string; value: string | null; tooltip: string }> = [];

  mounted(): void {
    this.subscribeButtonLabel();

    this.initialCurrency = this.currencyIso2 as CurrencyCode;
    this.initialPlanPrice = this.plan.price.price;

    this.featureList = [
      {
        title: this.$t('fields.pricing_table.feature_list.dynamic_codes_title').toString(),
        value: this.plan.codeLimit,
        tooltip: this.$t('fields.pricing_table.feature_list.dynamic_codes_description').toString(),
      },
      {
        title: this.$t('fields.pricing_table.feature_list.scans_title').toString(),
        value: this.plan.scanLimit,
        tooltip: this.$t('fields.pricing_table.feature_list.scans_description').toString(),
      },
      {
        title: this.$t('fields.pricing_table.feature_list.bulk_title').toString(),
        value: this.plan.bulkLimit,
        tooltip: this.$t('fields.pricing_table.feature_list.bulk_description').toString(),
      },
      {
        title: this.$t('fields.pricing_table.feature_list.api_requests_title').toString(),
        value: this.plan.apiLimit,
        tooltip: this.$t('fields.pricing_table.feature_list.api_requests_description').toString(),
      },
      {
        title: this.$t('fields.pricing_table.feature_list.users_title').toString(),
        value: this.plan.userLimit,
        tooltip: this.$t('fields.pricing_table.feature_list.users_description').toString(),
      },
    ];
  }

  get subscribeBtnLabel(): string {
    return this.subscribeButtonLabel().subscribeBtnLabel;
  }

  get subscribeBtnState(): string {
    return this.subscribeButtonLabel().subscribeBtnState;
  }

  get overrideCurrency(): string {
    let overrideCurrency = '';
    if (this.returnExchangeRate?.key) {
      overrideCurrency = this.returnExchangeRate.key;
    }
    return overrideCurrency;
  }

  get returnExchangeRate(): ExchangeRateInterface | null {
    const exchangeRate = this.exchangeRates.find((v) => v.key == this.currencyIso2);
    if (exchangeRate) {
      return exchangeRate as ExchangeRateInterface;
    }
    return null;
  }

  get returnDisplayPrice(): string {
    if (this.currencyIso2 === this.initialCurrency) {
      this.plan.displayOriginalPrice = new Currency(this.initialPlanPrice);
    } else if (this.returnExchangeRate?.value?.exchangeRate) {
      this.plan.displayOriginalPrice = new Currency(this.initialPlanPrice * this.returnExchangeRate.value.exchangeRate);
    }

    return this.plan.displayOriginalPrice.displayPriceMonthly({
      plan: this.plan,
      selectedInterval: this.selectedInterval,
      overrideCurrency: this.overrideCurrency,
    });
  }

  get showExcludedVatTaxTooltip(): boolean {
    const currenciesWithVatTooltip = ['$', '€', '£'];
    return currenciesWithVatTooltip.includes(this.returnSymbol);
  }

  get returnExcludedVatTaxLabel(): string {
    return this.$t(
      `fields.pricing_table.excluded_vat_tooltip.${this.returnSymbol === '$' ? 'excluded_tax' : 'excluded_vat'}`
    ).toString();
  }

  get returnYearlyCostWithCurrency(): string {
    const price = this.plan.price.price;
    switch (this.returnSymbol) {
      case '$':
      case 'R$':
      case '£': {
        return `${this.returnSymbol} ${price}`;
      }
      default:
        return `${price} ${this.returnSymbol}`;
    }
  }

  get returnSymbol(): string {
    let overrideCurrency: string | CurrencyCode = this.plan.currency;
    if (this.returnExchangeRate?.key) {
      overrideCurrency = this.returnExchangeRate.key;
    }
    return this.plan.price.displaySymbol({ overrideCurrency });
  }

  get returnStrikethroughPriceMonthly(): string | undefined {
    if (this.plan.strikethrough_price) {
      return (parseFloat(this.plan.strikethrough_price) / 12).toFixed(2);
    }
    return undefined;
  }

  /**
   * Return the original price of a plan.
   */
  get originalPlanPrice(): number | undefined {
    return this.plan?.originalPriceMonthly?.price || undefined;
  }

  /**
   * Sets the button label according to which plan the user currently has
   */
  subscribeButtonLabel(): { subscribeBtnLabel: string; subscribeBtnState: string } {
    const planState = this.stripeSubscriptionService.planState(this.plan.id);
    const { label, state } = labelForPlanState(planState);
    return { subscribeBtnLabel: String(this.$t(label)), subscribeBtnState: state };
  }

  planSelected(plan: Plan, state: string | 'continue' | 'reactivate'): void {
    if (state === 'continue') {
      const language = store.state?.auth?.user?.language;
      const user_id = store.state?.auth?.user?.id;
      const account = store.state?.account?.account;
      const price_currency_displayed = store.state?.auth?.team?.currency;
      trackCheckoutNowButtonPricingPage({
        industry_id: account?.industry_id ?? null,
        user_id,
        price_currency_displayed,
        language_code: language,
        plan_id: plan.id,
      });
      trackCheckoutNowButtonPricingPageStep1(window.dataLayer, plan.planName, plan.price.price);
    }

    if (state === 'reactivate') {
      this.reactivateModalShow = true;
    } else if (!this.stripeSubscriptionService.canDowngradePlan(plan.id)) {
      this.showDowngradeFailModal();
    } else {
      // choose selected plan
      this.$store.dispatch('subscription/setSubscriptionPlan', plan).then(() => {
        this.$router.push({ name: 'checkout_payment_form', params: { planId: plan.id } });
      });
    }
  }

  showDowngradeFailModal(): void {
    this.openDowngradeModal = true;
  }

  async onReactivate(): Promise<void> {
    await this.stripeSubscriptionService.initSubscription();
    this.onCancel();
  }

  onCancel(): void {
    this.reactivateModalShow = false;
    this.openDowngradeModal = false;
  }
}
